import { Tab, Tabs } from "@mui/material";
import MDBox from "components/MDBox";
import { WorksheetProduct } from "generated/graphql";
import useDeleteWorksheetProduct from "hooks/worksheetProducts/useDeleteWorksheetProduct";
import useDuplicateWorksheetProduct from "hooks/worksheetProducts/useDuplicateWorksheetProduct";
import useGetWorksheetProducts from "hooks/worksheetProducts/useGetWorksheetProducts";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import WorksheetProductEditorDrawer from "modules/worksheetProducts/WorksheetProductEditorDrawer";
import WorksheetProductsList from "modules/worksheetProducts/WorksheetProductsList";
import useDebouncedSearchTerm from "hooks/strings/useDebouncedSearchTerm";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import usePaginatedResources from "hooks/usePaginatedResources";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";

export default function WorksheetProductsPage() {
  const navigate = useNavigate();
  const { searchTerm, setSearchTerm, debouncedSearchTerm } = useDebouncedSearchTerm("");
  const [productTypeId, setProductTypeId] = useState("");
  const [editedProduct, setEditedProduct] = useState<WorksheetProduct | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const { offset } = usePaginatedVariables();
  const {
    data: products,
    loading,
    refetch,
    paginatorInfo,
  } = useGetWorksheetProducts({
    first: offset.first,
    page: offset.page,
    search: debouncedSearchTerm || undefined,
    organizationProductTypeId: productTypeId || undefined,
  });
  const pagination = usePaginatedResources({
    paginatorInfo,
    paginate: offset.paginate,
  });
  const [duplicateWorksheetProduct] = useDuplicateWorksheetProduct();
  const [deleteWorksheetProduct] = useDeleteWorksheetProduct();

  const handleCreate = useCallback(() => {
    setEditedProduct(null);
    setIsEditing(true);
  }, []);

  const handleEdit = useCallback((product: WorksheetProduct) => {
    setEditedProduct(product);
    setIsEditing(true);
  }, []);

  const handleClose = useCallback(() => setIsEditing(false), []);

  const handleDuplicate = useCallback(
    async (id: string) => {
      await duplicateWorksheetProduct({ variables: { id } });
      refetch();
    },
    [duplicateWorksheetProduct, refetch]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteWorksheetProduct({ variables: { id } });
      refetch();
    },
    [deleteWorksheetProduct, refetch]
  );

  return (
    <DashboardPage>
      <MDBox mb={2}>
        <Tabs value="worksheet" textColor="primary" indicatorColor="primary">
          <Tab label="Products" value="products" onClick={() => navigate("/product-types")} />
          <Tab label="Worksheet" value="worksheet" />
        </Tabs>
      </MDBox>

      <WorksheetProductsList
        products={products}
        loading={loading}
        search={searchTerm}
        onSearchChange={setSearchTerm}
        productTypeId={productTypeId}
        onProductTypeChange={setProductTypeId}
        onCreate={handleCreate}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
      />

      <WorksheetProductEditorDrawer
        open={isEditing}
        product={editedProduct}
        onClose={handleClose}
        onSaved={refetch}
      />

      <MDBox display="flex" justifyContent="flex-end" mt={1}>
        {pagination.component}
      </MDBox>
    </DashboardPage>
  );
}
